import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase, Event, UserBadge } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import BottomNav from '../components/BottomNav';
import ConnectionsList from '../components/ConnectionsList';
import Badge, { BadgeDisplay } from '../components/Badge';
import ActivityFeed from '../components/ActivityFeed';
import { SkeletonCheckInCard } from '../components/SkeletonLoader';
import Button from '../components/ui/Button';
import { User, Award, Calendar, Edit, Trash2, Plus, Settings as SettingsIcon, ArrowLeft, UserPlus, Check, X, FileText, Activity, NotebookPen, Flame, ChevronDown, ChevronUp } from 'lucide-react';

export default function Profile() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { currentUser, checkIns } = useEmberStore();
  const [userSessions, setUserSessions] = useState<any[]>([]);
  const [userCheckIns, setUserCheckIns] = useState<any[]>([]);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [userReminders, setUserReminders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  
  // For viewing other users' profiles
  const [viewingUser, setViewingUser] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [processingConnection, setProcessingConnection] = useState(false);
  
  // Badge system
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);

  // UI state for collapsible sections
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  const [checkInsExpanded, setCheckInsExpanded] = useState(false);
  const [remindersExpanded, setRemindersExpanded] = useState(false);

  const sectionHeaderStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.75rem',
    flexWrap: 'wrap',
    marginBottom: '1rem',
  };

  const rectangularButtonStyle: CSSProperties = {
    borderRadius: '6px',
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
  };

  useEffect(() => {
    if (userId && userId !== currentUser?.id) {
      // Viewing another user's profile
      loadOtherUserProfile(userId);
    } else {
      // Viewing own profile
      loadUserSessions();
      loadUserCheckIns();
      loadUserEvents();
      loadUserReminders();
    }
    loadUserBadges();
    // Load global user count (profiles count)
    (async () => {
      try {
        const { count, error } = await supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true });
        if (error) throw error;
        setTotalUsers(typeof count === 'number' ? count : null);
      } catch (err) {
        console.warn('User count load skipped:', err);
      }
    })();
  }, [currentUser, userId]);

  async function loadOtherUserProfile(targetUserId: string) {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_user_profile', {
        target_user_id: targetUserId
      });

      if (error) throw error;
      if (data && data.length > 0) {
        const profile = data[0];
        setViewingUser(profile);
        setConnectionStatus(profile);
        
        if (profile.can_view_profile) {
          // Load their public check-ins and events
          loadOtherUserCheckIns(targetUserId);
          loadOtherUserEvents(targetUserId);
        }
      } else {
        // Profile not found or not accessible
        setViewingUser(null);
      }
    } catch (err) {
      console.error('Error loading other user profile:', err);
      setViewingUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadOtherUserCheckIns(targetUserId: string) {
    try {
      const { data, error } = await supabase
        .from('checkins')
        .select(`
          *,
          tobacco:tobaccos(*)
        `)
        .eq('user_id', targetUserId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      if (data) setUserCheckIns(data);
    } catch (err) {
      console.error('Error loading other user check-ins:', err);
    }
  }

  async function loadOtherUserEvents(targetUserId: string) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          venue:venues(*)
        `)
        .eq('creator_id', targetUserId)
        .eq('is_public', true) // Only public events
        .order('start_datetime', { ascending: true })
        .limit(10);

      if (error) throw error;
      if (data) setUserEvents(data as any);
    } catch (err) {
      console.error('Error loading other user events:', err);
    }
  }

  async function loadUserBadges() {
    try {
      const targetUserId = userId || currentUser?.id;
      if (!targetUserId) return;

      const { data, error } = await supabase.rpc('get_user_badges', {
        user_uuid: targetUserId
      });

      if (error) throw error;
      if (data) setUserBadges(data);
    } catch (err) {
      console.error('Error loading user badges:', err);
    }
  }

  async function loadUserCheckIns() {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('checkins')
        .select(`
          *,
          tobacco:tobaccos(*)
        `)
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (data) setUserCheckIns(data);
    } catch (err) {
      console.error('Error loading user check-ins:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadUserSessions() {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          cellar:cellar_tobacco(*)
        `)
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      if (data) setUserSessions(data as any);
    } catch (err) {
      console.error('Error loading user sessions:', err);
    }
  }

  async function loadUserEvents() {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          venue:venues(*)
        `)
        .eq('creator_id', currentUser.id)
        .order('start_datetime', { ascending: true });

      if (error) throw error;
      if (data) setUserEvents(data as any);
    } catch (err) {
      console.error('Error loading user events:', err);
    }
  }

  async function loadUserReminders() {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('cellar_tobacco')
        .select('*')
        .eq('user_id', currentUser.id)
        .not('reminder_at', 'is', null)
        .order('reminder_at', { ascending: true });

      if (error) throw error;
      if (data) setUserReminders(data as any);
    } catch (err) {
      console.error('Error loading user reminders:', err);
    }
  }

  async function showConfirmModal(title: string, message: string, confirmLabel = 'Delete') {
    return await new Promise<boolean>((resolve) => {
      const modal = document.createElement('div');
      modal.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;`;
    const content = document.createElement('div');
    content.style.cssText = `background:#1a1a1a;border:1px solid #e4574d;border-radius:12px;padding:2rem;max-width:400px;width:100%;text-align:center;color:#ffffff;`;
    content.innerHTML = `
      <h3 style="font-size:1.25rem;margin-bottom:1rem;color:#e4574d;">${title}</h3>
        <p style="color:#cfcfcf;margin-bottom:1.5rem;">${message}</p>
      <div style="display:flex;gap:1rem;justify-content:center;">
        <button id="cancel" style="padding:0.75rem 1.5rem;background:transparent;color:#999;border:1px solid #666;border-radius:6px;cursor:pointer;font-weight:600;">Cancel</button>
        <button id="confirm" style="padding:0.75rem 1.5rem;background:#e4574d;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:600;">${confirmLabel}</button>
      </div>
      `;
      modal.appendChild(content);
      document.body.appendChild(modal);
      content.querySelector('#cancel')?.addEventListener('click', () => { modal.remove(); resolve(false); });
      content.querySelector('#confirm')?.addEventListener('click', () => { modal.remove(); resolve(true); });
    });
  }

  async function deleteEvent(eventId: string) {
    const ok = await showConfirmModal('Delete Event', 'Are you sure you want to delete this event? This action cannot be undone.');
    if (!ok) return;
    
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
      
      // Reload events
      loadUserEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
    }
  }

  // Connection action handlers
  async function handleSendConnectionRequest() {
    if (!viewingUser) return;
    
    setProcessingConnection(true);
    try {
      const { error } = await supabase.rpc('send_connection_request', {
        target_user: viewingUser.id
      });

      if (error) throw error;
      
      setConnectionStatus((prev: any) => ({ ...prev, connection_status: 'pending', can_send_request: false }));
      toast.success('Connection request sent!');
    } catch (err) {
      console.error('Error sending connection request:', err);
      toast.error('Failed to send connection request');
    } finally {
      setProcessingConnection(false);
    }
  }

  async function handleAcceptConnection() {
    if (!connectionStatus?.connection_id) return;
    
    setProcessingConnection(true);
    try {
      const { error } = await supabase.rpc('accept_connection_request', {
        connection_id: connectionStatus.connection_id
      });

      if (error) throw error;
      
      setConnectionStatus((prev: any) => ({ ...prev, connection_status: 'accepted' }));
      toast.success('Connection accepted!');
    } catch (err) {
      console.error('Error accepting connection:', err);
      toast.error('Failed to accept connection');
    } finally {
      setProcessingConnection(false);
    }
  }

  async function handleRemoveConnection() {
    if (!connectionStatus?.connection_id) return;
    
    setProcessingConnection(true);
    try {
      const { error } = await supabase.rpc('remove_connection', {
        connection_id: connectionStatus.connection_id
      });

      if (error) throw error;
      
      setConnectionStatus((prev: any) => ({ ...prev, connection_status: 'none', can_send_request: true }));
      toast.success('Connection removed');
    } catch (err) {
      console.error('Error removing connection:', err);
      toast.error('Failed to remove connection');
    } finally {
      setProcessingConnection(false);
    }
  }

  const isViewingOwnProfile = !userId || userId === currentUser?.id;
  const displayUser = isViewingOwnProfile ? currentUser : viewingUser;

  const stats = {
    totalCheckIns: userCheckIns.length,
    uniqueTobaccos: new Set(userCheckIns.map((c) => c.tobacco_id)).size,
    avgRating: userCheckIns.length > 0
      ? (userCheckIns.reduce((sum, c) => sum + c.rating, 0) / userCheckIns.length).toFixed(1)
      : 0,
  };

  useEffect(() => {
    if (userSessions.length === 0) {
      setSessionsExpanded(false);
    }
  }, [userSessions.length]);

  useEffect(() => {
    if (userCheckIns.length === 0) {
      setCheckInsExpanded(false);
    }
  }, [userCheckIns.length]);

  // Loading state
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
        <div className="container" style={{ paddingTop: '2rem', textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '1rem', color: '#999' }}>Loading profile...</p>
        </div>
        <BottomNav />
      </div>
    );
  }

  // Profile not found or not accessible
  if (!isViewingOwnProfile && !viewingUser) {
    return (
      <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
        <header style={{
          padding: '1rem',
          borderBottom: '1px solid #6b4423',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
          >
            <ArrowLeft size={24} />
          </button>
          <h1 style={{ fontSize: '1.5rem' }}>Profile Not Found</h1>
        </header>
        <div className="container" style={{ paddingTop: '2rem', textAlign: 'center' }}>
          <User size={64} style={{ margin: '0 auto 1rem', opacity: 0.3, color: '#999' }} />
          <h2 style={{ marginBottom: '1rem' }}>Profile Not Available</h2>
          <p style={{ color: '#999', marginBottom: '2rem' }}>
            This profile is private or doesn't exist.
          </p>
          <Button variant="secondary" onClick={() => navigate('/discover')}>
            Discover Users
          </Button>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #6b4423',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {!isViewingOwnProfile && (
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer' }}
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <h1 style={{ fontSize: '1.5rem' }}>
            {isViewingOwnProfile ? 'Profile' : `${displayUser?.username}'s Profile`}
          </h1>
        </div>
        {/* Center badge showing total users */}
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          {typeof totalUsers === 'number' && (
            <span style={{ fontSize: '0.85rem', color: 'var(--color-secondary)', backgroundColor: 'rgba(63,160,201,0.08)', border: '1px solid var(--color-secondary)', borderRadius: '999px', padding: '0.2rem 0.6rem', whiteSpace: 'nowrap' }}>
              {totalUsers.toLocaleString()} users!
            </span>
          )}
        </div>
        {isViewingOwnProfile && (
          <Link to="/settings" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem',
            backgroundColor: 'var(--color-card)',
            border: '1px solid var(--color-secondary)',
            borderRadius: '8px',
            color: 'var(--color-secondary)',
            textDecoration: 'none'
          }}
            title="Settings"
          >
            <SettingsIcon size={18} color="var(--color-secondary)" />
          </Link>
        )}
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#5a3310',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            overflow: 'hidden'
          }}>
            {displayUser?.avatar_url ? (
              <img src={displayUser.avatar_url} alt="Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <User size={40} color="#ff9500" />
            )}
          </div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            {displayUser?.username}
          </h2>
          <p style={{ color: 'var(--color-secondary)', marginBottom: '1rem' }}>
            {displayUser?.smoker_type === 'both'
              ? 'Pipe & Cigar Enthusiast'
              : `${displayUser?.smoker_type?.charAt(0).toUpperCase()}${displayUser?.smoker_type?.slice(1)} Enthusiast`}
          </p>

          {displayUser?.bio && (
            <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FileText size={16} color="var(--color-secondary)" />
                <span style={{ fontSize: '0.875rem', color: 'var(--color-secondary)', fontWeight: '600' }}>About</span>
              </div>
              <p style={{
                fontSize: '0.875rem',
                color: '#cfcfcf',
                lineHeight: '1.5',
                backgroundColor: 'rgba(11, 11, 11, 0.3)',
                padding: '0.75rem',
                borderRadius: '8px',
                border: '1px solid var(--color-border)'
              }}>
                {displayUser.bio}
              </p>
            </div>
          )}

          {!isViewingOwnProfile && connectionStatus && (
            <div style={{ marginTop: '1.5rem' }}>
              {connectionStatus.connection_status === 'none' && connectionStatus.can_send_request && (
                <Button
                  variant="primary"
                  onClick={handleSendConnectionRequest}
                  loading={processingConnection}
                  iconLeft={<UserPlus size={16} />}
                >
                  Connect
                </Button>
              )}
              {connectionStatus.connection_status === 'pending' && (
                <div style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'rgba(255, 165, 0, 0.1)',
                  border: '1px solid rgba(255, 165, 0, 0.3)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: '#ffa500',
                  fontWeight: '500'
                }}>
                  Connection Request Sent
                </div>
              )}
              {connectionStatus.connection_status === 'accepted' && (
                <div style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: 'rgba(107, 175, 74, 0.1)',
                  border: '1px solid rgba(107, 175, 74, 0.3)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  color: 'var(--color-success)',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}>
                  <Check size={16} />
                  Connected
                </div>
              )}
            </div>
          )}
        </div>

        {isViewingOwnProfile && (
          <section style={{ marginBottom: '2rem' }}>
            <div style={sectionHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <NotebookPen size={24} color="var(--color-secondary)" />
                <h3 style={{ fontSize: '1.25rem', margin: 0, color: 'var(--color-secondary)' }}>Your Sessions</h3>
              </div>
              {userSessions.length > 0 && (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setSessionsExpanded((prev) => !prev)}
                  iconRight={sessionsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  style={rectangularButtonStyle}
                >
                  {sessionsExpanded ? 'Hide Sessions' : `View Sessions (${userSessions.length})`}
                </Button>
              )}
              <Link to="/sessions/new" style={{ textDecoration: 'none' }}>
                <Button
                  variant="primary"
                  size="small"
                  iconLeft={<Plus size={16} />}
                  style={rectangularButtonStyle}
                >
                  Create Session
                </Button>
              </Link>
            </div>
            {loading && userSessions.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                Loading sessions...
              </div>
            ) : userSessions.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#d3de5e', marginBottom: '1rem' }}>
                  No sessions yet. Capture your first journal entry.
                </p>
                <Link to="/sessions/new" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="primary"
                    size="small"
                    iconLeft={<Plus size={16} />}
                    style={{ ...rectangularButtonStyle, display: 'inline-flex' }}
                  >
                    Create Session
                  </Button>
                </Link>
              </div>
            ) : sessionsExpanded ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {userSessions.map((session: any) => {
                  const title = session.cellar
                    ? `${session.cellar.brand} - ${session.cellar.blend_name}`
                    : session.tobacco_manual || 'Session';
                  const ratingRaw = typeof session.rating_10 === 'number'
                    ? session.rating_10
                    : typeof session.rating_small === 'number'
                      ? session.rating_small * 2
                      : null;
                  return (
                    <div key={session.id} className="card" style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>{title}</h4>
                          <p style={{ fontSize: '0.8rem', color: '#999' }}>
                            {new Date(session.created_at).toLocaleDateString()} • {ratingRaw ? `Rated ${ratingRaw}/10` : 'No rating'}
                          </p>
                        </div>
                      </div>
                      {session.notes && (
                        <p style={{ fontSize: '0.875rem', color: '#cfcfcf', lineHeight: 1.5 }}>{session.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card" style={{ padding: '1.5rem', textAlign: 'center', color: '#999' }}>
                Tap "View Sessions" to see {userSessions.length === 1 ? 'your session' : `all ${userSessions.length} sessions`}.
              </div>
            )}
          </section>
        )}

        {isViewingOwnProfile && (
          <section style={{ marginBottom: '2rem' }}>
            <div style={sectionHeaderStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={24} color="var(--color-secondary)" />
                <h3 style={{ fontSize: '1.25rem', margin: 0, color: 'var(--color-secondary)' }}>Your Reminders</h3>
              </div>
              {userReminders.length > 0 && (
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setRemindersExpanded((prev) => !prev)}
                  iconRight={remindersExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  style={rectangularButtonStyle}
                >
                  {remindersExpanded ? 'Hide Reminders' : `View Reminders (${userReminders.length})`}
                </Button>
              )}
            </div>
            {userReminders.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <p style={{ color: '#d3de5e', marginBottom: '0.5rem' }}>No reminders set yet.</p>
                <p style={{ color: '#999', fontSize: '0.875rem' }}>Set reminders from your cellar items to see them here.</p>
              </div>
            ) : remindersExpanded ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {userReminders.map((r) => (
                  <div key={r.id} className="card" style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div>
                        <h4 style={{ fontSize: '1rem', margin: 0, marginBottom: '0.25rem' }}>
                          {r.brand} - {r.blend_name}
                        </h4>
                        <p style={{ fontSize: '0.8rem', color: '#999', margin: 0 }}>
                          🔔 Reminder: {new Date(r.reminder_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {r.reminder_notes && (
                      <p style={{ fontSize: '0.875rem', color: '#cfcfcf', marginTop: '0.5rem' }}>{r.reminder_notes}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card" style={{ padding: '1.5rem', textAlign: 'center', color: '#999' }}>
                Tap &quot;View Reminders&quot; to see all of your cellar reminders.
              </div>
            )}
          </section>
        )}

        <section style={{ marginBottom: '2rem' }}>
          <div style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Flame size={24} color="var(--color-secondary)" />
              <h3 style={{ fontSize: '1.25rem', margin: 0, color: 'var(--color-secondary)' }}>
                {isViewingOwnProfile ? 'Your Check-ins' : `${displayUser?.username}'s Check-ins`}
              </h3>
            </div>
            {userCheckIns.length > 0 && (
              <Button
                variant="ghost"
                size="small"
                onClick={() => setCheckInsExpanded((prev) => !prev)}
                iconRight={checkInsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                style={rectangularButtonStyle}
              >
                {checkInsExpanded ? 'Hide Check-ins' : `View Check-ins (${userCheckIns.length})`}
              </Button>
            )}
            {isViewingOwnProfile && (
              <Link to="/checkin" style={{ textDecoration: 'none' }}>
                <Button
                  variant="primary"
                  size="small"
                  iconLeft={<Plus size={16} />}
                  style={rectangularButtonStyle}
                >
                  Create Check-in
                </Button>
              </Link>
            )}
          </div>
          {loading ? (
            <>
              <SkeletonCheckInCard />
              <SkeletonCheckInCard />
            </>
          ) : userCheckIns.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#d3de5e', marginBottom: '1rem' }}>
                {isViewingOwnProfile ? 'No check-ins yet. Create your first one!' : 'No public check-ins yet.'}
              </p>
              {isViewingOwnProfile && (
                <Link to="/checkin" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="primary"
                    size="small"
                    iconLeft={<Plus size={16} />}
                    style={{ ...rectangularButtonStyle, display: 'inline-flex' }}
                  >
                    Create Check-in
                  </Button>
                </Link>
              )}
            </div>
          ) : checkInsExpanded ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {userCheckIns.map((checkIn) => (
                <div key={checkIn.id} className="card" style={{ padding: '1rem' }}>
                  <h4 style={{ fontSize: '1rem', marginBottom: '0.25rem' }}>
                    {checkIn.tobacco?.name || checkIn.tobacco_manual || 'Check-in'}
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: '#d3de5e', marginBottom: '0.5rem' }}>
                    {checkIn.tobacco?.brand ? `${checkIn.tobacco.brand} • ` : ''}Rating: {checkIn.rating}/5
                  </p>
                  {checkIn.notes && (
                    <p style={{ fontSize: '0.875rem' }}>{checkIn.notes}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="card" style={{ padding: '1.5rem', textAlign: 'center', color: '#999' }}>
              Tap "View Check-ins" to see {userCheckIns.length === 1 ? 'this check-in' : `all ${userCheckIns.length} check-ins`}.
            </div>
          )}
        </section>

        <section style={{ marginBottom: '2rem' }}>
          <div style={sectionHeaderStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={24} color="var(--color-secondary)" />
              <h3 style={{ fontSize: '1.25rem', margin: 0, color: 'var(--color-secondary)' }}>
                {isViewingOwnProfile ? 'My Events' : `${displayUser?.username}'s Events`}
              </h3>
            </div>
            {isViewingOwnProfile && (
              <Link to="/create-event" style={{ textDecoration: 'none' }}>
                <Button
                  variant="primary"
                  size="small"
                  iconLeft={<Plus size={16} />}
                  style={rectangularButtonStyle}
                >
                  Create Event
                </Button>
              </Link>
            )}
          </div>

          {userEvents.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: '#d3de5e', marginBottom: '1rem' }}>
                {isViewingOwnProfile ? 'No events created yet.' : 'No public events yet.'}
              </p>
              {isViewingOwnProfile && (
                <Link to="/create-event" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="primary"
                    size="small"
                    iconLeft={<Plus size={16} />}
                    style={{ ...rectangularButtonStyle, display: 'inline-flex' }}
                  >
                    Create Event
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {userEvents.map((event) => (
                <div key={event.id} className="card" style={{ padding: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                        {event.title}
                      </h4>
                      <p style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '0.5rem' }}>
                        📍 {event.venue?.name}
                      </p>
                      <p style={{ fontSize: '0.875rem', color: '#d3de5e' }}>
                        📅 {new Date(event.start_datetime).toLocaleDateString()} at {new Date(event.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {isViewingOwnProfile && (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => navigate(`/edit-event/${event.id}`)}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: 'var(--color-card)',
                            border: '1px solid var(--color-accent)',
                            color: 'var(--color-accent)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => deleteEvent(event.id)}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: 'var(--color-card)',
                          border: '1px solid var(--color-danger)',
                          color: 'var(--color-danger)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#cfcfcf', lineHeight: '1.5' }}>
                    {event.description}
                  </p>
                  {event.max_attendees && (
                    <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.5rem' }}>
                      Max {event.max_attendees} attendees
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-secondary)' }}>
          <Award size={24} color="var(--color-secondary)" />
          Stats
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
          <div className="card" style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9500' }}>
              {stats.totalCheckIns}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#d3de5e' }}>Check-ins</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9500' }}>
              {stats.uniqueTobaccos}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#d3de5e' }}>Unique</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ff9500' }}>
              {stats.avgRating}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#d3de5e' }}>Avg Rating</div>
          </div>
        </div>

        {isViewingOwnProfile && <ConnectionsList />}

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Award size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
              {isViewingOwnProfile ? 'Your Badges' : `${displayUser?.username}'s Badges`}
            </h3>
          </div>
          <BadgeDisplay badges={userBadges} />
        </div>

        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Activity size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>
              {isViewingOwnProfile ? 'Your Activity' : `${displayUser?.username}'s Activity`}
            </h3>
          </div>
          <ActivityFeed userId={userId} limit={10} />
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

